// iter619 — Public auditor-share view. No login required. Pulls the
// signed JSON pack and renders a read-only summary the auditor can
// browse before downloading the full Excel/CSV.

import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, FileDown, ShieldCheck, AlertTriangle } from 'lucide-react';

export default function AuditorSharePage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const base = process.env.REACT_APP_BACKEND_URL || '';
        const res = await fetch(`${base}/api/auditor-share/${token}`);
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.detail || `HTTP ${res.status}`);
        }
        setData(await res.json());
      } catch (e) {
        setError(e.message);
      }
    })();
  }, [token]);

  const download = (format) => {
    const base = process.env.REACT_APP_BACKEND_URL || '';
    window.open(`${base}/api/auditor-share/${token}?format=${format}`, '_blank');
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-stone-50">
        <Card className="max-w-md w-full">
          <CardContent className="p-6 text-center space-y-3">
            <AlertTriangle size={36} className="mx-auto text-rose-500" />
            <h1 className="text-lg font-bold font-outfit">Share link unavailable</h1>
            <p className="text-sm text-muted-foreground">{error}</p>
            <p className="text-xs text-muted-foreground">Ask the business to generate a fresh link from their Monthly Reconciliation Tracker.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground">
        <Loader2 className="animate-spin mr-2" size={16} /> Loading auditor pack…
      </div>
    );
  }

  const pack = data.pack;
  const s = pack.summary;
  const tile = (label, value) => (
    <div className="bg-white rounded-xl border p-3">
      <div className="text-[11px] uppercase text-muted-foreground tracking-wider">{label}</div>
      <div className="text-lg font-bold mt-0.5">{Number(value || 0).toLocaleString()}</div>
    </div>
  );

  return (
    <div className="min-h-screen bg-stone-50 p-4 sm:p-8" data-testid="auditor-share-page">
      <div className="max-w-4xl mx-auto space-y-4">
        <div className="bg-gradient-to-br from-sky-600 to-sky-800 text-white rounded-2xl p-5 shadow-lg">
          <div className="flex items-center gap-3">
            <ShieldCheck size={28} />
            <div className="min-w-0">
              <h1 className="text-xl sm:text-2xl font-bold font-outfit truncate">Auditor Pack · {pack.month}</h1>
              <p className="text-[12px] opacity-90">Period: {pack.period}</p>
            </div>
            <div className="ml-auto text-right shrink-0">
              <div className="text-[10px] uppercase tracking-wider opacity-80">Views</div>
              <div className="text-lg font-bold">{data.share.view_count}</div>
            </div>
          </div>
          {pack.closed && (
            <div className="mt-3 inline-flex items-center gap-1.5 bg-emerald-400/30 text-white text-[11px] px-2.5 py-1 rounded-full">
              ✓ Month closed by {pack.closed_by} on {new Date(pack.closed_at).toLocaleDateString()}
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {tile('Sales total', s.sales_total)}
          {tile('Expenses total', s.expenses_total)}
          {tile('Journal entries', s.journal_entries_count)}
          {tile('Bank statements', s.bank_statements_count)}
          {tile('Settlements', s.settlements_count)}
          {tile('Customers w/ credit', s.customers_with_credit)}
          {tile('Suppliers w/ credit', s.suppliers_with_credit)}
          {tile('Branches', (pack.branches || []).length)}
        </div>

        <Card>
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <h2 className="font-bold font-outfit">Download the full pack</h2>
              <Badge variant="outline">Expires {new Date(data.share.expires_at).toLocaleDateString()}</Badge>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
              <Button onClick={() => download('excel')} className="rounded-lg" data-testid="share-dl-excel">
                <FileDown size={14} className="mr-1" /> Excel
              </Button>
              <Button onClick={() => download('csv')} variant="outline" className="rounded-lg" data-testid="share-dl-csv">
                <FileDown size={14} className="mr-1" /> CSV (.zip)
              </Button>
              <Button onClick={() => download('json')} variant="outline" className="rounded-lg" data-testid="share-dl-json">
                <FileDown size={14} className="mr-1" /> JSON
              </Button>
            </div>
            {pack.truncation_notes?.journal_entries_truncated && (
              <p className="text-[11px] text-amber-700">
                Note: {s.journal_entries_count} journal entries; only first 5,000 included in the pack.
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <h3 className="font-bold font-outfit mb-2">Branches</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-[12px]">
              {(pack.branches || []).map((b) => (
                <div key={b.id} className="bg-stone-100 rounded-lg p-2">
                  <strong>{b.name}</strong>
                  <span className="text-muted-foreground"> · {b.country_code?.toUpperCase()} · {b.currency_code}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <p className="text-center text-[10px] text-muted-foreground">
          This is a read-only auditor view. The business holds the source data. Contact them if anything needs amending.
        </p>
      </div>
    </div>
  );
}
